import React, { useEffect, useState } from "react";

import {
	Avatar,
	CardHeader,
	IconButton,
	Popover,
	Box,
	Typography,
	Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import withMediaToken from "../../services/mediaUrl";
import { emitClientUpdated, onClientUpdated } from "../../services/clientEvents";
import StatusChip from "../StatusChip";

const useStyles = makeStyles(theme => ({
	wrapper: {
		display: "flex",
		flexDirection: "column",
		flex: 1,
		minWidth: 0,
	},
	header: {
		padding: theme.spacing(1, 2),
		"& .MuiCardHeader-content": { minWidth: 0 },
	},
	// Quebra linha para caber tudo. Antes rolava na horizontal para poupar
	// altura, mas o corte era invisível: sem barra de rolagem à vista, as tags
	// que não coubessem simplesmente sumiam pela direita — e uma tag que não
	// aparece é pior que uma linha a mais de cabeçalho, porque a pessoa decide
	// achando que viu tudo.
	chipsRow: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "center",
		gap: 4,
		padding: "0 16px 6px 72px",
		"& > *": { flexShrink: 0 },
		[theme.breakpoints.down("xs")]: {
			paddingLeft: 16,
		},
	},
	tagsPopover: {
		padding: theme.spacing(1, 1.5),
		minWidth: 240,
		maxWidth: 320,
		display: "flex",
		flexDirection: "column",
		gap: 6,
	},
	tagsList: {
		display: "flex",
		flexWrap: "wrap",
		gap: 4,
	},
	editTagsBtn: {
		padding: 2,
	},
}));

const TicketInfo = ({ contact, ticket, onClick }) => {
	const classes = useStyles();
	const clientId = contact?.clientId;

	const [client, setClient] = useState(null);
	const [allTags, setAllTags] = useState([]);
	const [anchorEl, setAnchorEl] = useState(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!clientId) {
			setClient(null);
			return;
		}
		let active = true;
		(async () => {
			try {
				const { data } = await api.get(`/clients/${clientId}`);
				if (active) setClient(data);
			} catch (err) {
				/* silencioso */
			}
		})();
		return () => {
			active = false;
		};
	}, [clientId]);

	useEffect(() => {
		if (!clientId) return;
		(async () => {
			try {
				const { data } = await api.get("/client-tags");
				setAllTags(data || []);
			} catch (err) {
				/* silencioso */
			}
		})();
	}, [clientId]);

	useEffect(() => {
		if (!clientId) return undefined;
		return onClientUpdated((updated) => {
			if (updated && updated.id === clientId) {
				setClient((prev) => ({ ...(prev || {}), ...updated }));
			}
		});
	}, [clientId]);

	const handleOpenTags = e => {
		e.stopPropagation();
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTags = () => setAnchorEl(null);

	const toggleTag = async tag => {
		if (!client) return;
		const currentIds = (client.tags || []).map(t => t.id);
		const exists = currentIds.includes(tag.id);
		const nextIds = exists
			? currentIds.filter(id => id !== tag.id)
			: [...currentIds, tag.id];
		setSaving(true);
		try {
			const { data } = await api.put(`/clients/${client.id}`, {
				tagIds: nextIds,
			});
			setClient(prev => ({ ...prev, ...data }));
			emitClientUpdated(data);
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const status = client?.clientStatus || client?.status;
	const tags = client?.tags || [];

	// O seletor listava só o catálogo. Uma tag aplicada ao cliente que tenha
	// saído do cadastro depois some da lista e fica sem como ser desmarcada por
	// ali — por isso as aplicadas entram mesmo quando não constam mais.
	const opcoesDeTag = [
		...allTags,
		...tags.filter(t => !allTags.some(a => a.id === t.id)),
	];

	return (
		<div className={classes.wrapper}>
			<CardHeader
				className={classes.header}
				onClick={onClick}
				style={{ cursor: "pointer" }}
				titleTypographyProps={{ noWrap: true }}
				subheaderTypographyProps={{ noWrap: true }}
				avatar={<Avatar src={withMediaToken(contact.profilePicUrl)} alt="contact_image" />}
				title={`${contact.name} #${ticket.id}`}
				subheader={
					ticket.user &&
					`${i18n.t("messagesList.header.assignedTo")} ${ticket.user.name}`
				}
			/>
			{clientId && (status || tags.length > 0 || allTags.length > 0) && (
				<div
					className={classes.chipsRow}
					onClick={e => e.stopPropagation()}
				>
					{status && (
						<StatusChip label={status.name} color={status.color} />
					)}
					{tags.map(tag => (
						<StatusChip
							key={tag.id}
							label={tag.name}
							color={tag.color}
							disabled={saving}
							onDelete={() => toggleTag(tag)}
						/>
					))}
					<IconButton
						className={classes.editTagsBtn}
						size="small"
						onClick={handleOpenTags}
						title={tags.length ? "Alterar tags" : "Adicionar tags"}
					>
						<LocalOfferIcon fontSize="small" />
					</IconButton>
					<Popover
						open={Boolean(anchorEl)}
						anchorEl={anchorEl}
						onClose={handleCloseTags}
						anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
						transformOrigin={{ vertical: "top", horizontal: "left" }}
					>
						<Box className={classes.tagsPopover}>
							<Typography variant="caption" color="textSecondary">
								Tags do cliente
							</Typography>
							<Divider />
							<div className={classes.tagsList}>
								{opcoesDeTag.length === 0 && (
									<Typography variant="caption" color="textSecondary">
										Nenhuma tag cadastrada
									</Typography>
								)}
								{opcoesDeTag.map(tag => {
									const selected = tags.some(t => t.id === tag.id);
									return (
										<StatusChip
											key={tag.id}
											label={tag.name}
											color={tag.color}
											selected={selected}
											disabled={saving}
											onClick={() => toggleTag(tag)}
										/>
									);
								})}
							</div>
						</Box>
					</Popover>
				</div>
			)}
		</div>
	);
};

export default TicketInfo;
